// src/tabs/MaquinasSubTab.js
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Settings, Plus, Trash2, Calendar, User, Eye, History, ShieldAlert, CheckCircle, Wrench, AlertTriangle, AlertCircle, MapPin, Hash, Edit3 } from "lucide-react";
import { pb } from "../services/pocketbase";

export default function MaquinasSubTab({ user }) {
  const [machines, setMachines] = useState([]);
  const [maintenances, setMaintenances] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter States
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [activeMachineId, setActiveMachineId] = useState(null);

  // Modal States
  const [isMachineModalOpen, setIsMachineModalOpen] = useState(false);
  const [isMaintModalOpen, setIsMaintModalOpen] = useState(false);
  const [isExecModalOpen, setIsExecModalOpen] = useState(false);

  // Editing States
  const [editingMachineId, setEditingMachineId] = useState(null);
  const [editingMaintPlanId, setEditingMaintPlanId] = useState(null);

  // Form States (New Machine)
  const [machForm, setMachForm] = useState({
    nome: "", marca: "", modelo: "", numero_serie: "",
    data_aquisicao: "", valor_aquisicao: "", fornecedor: "",
    localizacao: "", status: "ativa", descricao: "", especificacoes: ""
  });

  // Form States (New Plan)
  const [maintForm, setMaintForm] = useState({
    tipo: "preventiva", frequencia: "mensal", descricao: "",
    responsavel: "", data_ultima_execucao: new Date().toISOString().split("T")[0]
  });

  // Form States (Execute Maintenance)
  const [execForm, setExecForm] = useState({
    executado_por: user?.name || "", observacoes: "", duracao_minutos: "30"
  });
  const [activeMaintPlan, setActiveMaintPlan] = useState(null);

  // Load everything
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [machRecords, maintRecords, alertRecords] = await Promise.all([
        pb.collection("maquinas").getFullList({ sort: "-created" }),
        pb.collection("manutencoes").getFullList({ sort: "-created" }),
        pb.collection("alertas_manutencao").getFullList({ filter: "status='ativo'", sort: "data_prevista" })
      ]);

      setMachines(machRecords);
      setMaintenances(maintRecords);
      setAlerts(alertRecords);

      if (machRecords.length > 0 && !activeMachineId) {
        setActiveMachineId(machRecords[0].id);
      }
      
      // Run Alert Synchronization Routine
      await syncAlertsRoutine(machRecords, maintRecords);
    } catch (e) {
      console.error("Erro loading machines data:", e);
    } finally {
      setLoading(false);
    }
  }, [activeMachineId]);

  useEffect(() => {
    loadData();
  }, []);

  // Helper date adder
  const calculateNextDate = (lastDateStr, freq) => {
    if (!lastDateStr) return new Date().toISOString().split("T")[0];
    const d = new Date(lastDateStr);
    
    // Add offset days based on UTC
    const addDays = (date, days) => {
      const result = new Date(date);
      result.setUTCDate(result.getUTCDate() + days);
      return result.toISOString().split("T")[0];
    };

    switch (freq) {
      case "diaria": return addDays(d, 1);
      case "semanal": return addDays(d, 7);
      case "mensal": return addDays(d, 30);
      case "trimestral": return addDays(d, 90);
      case "semestral": return addDays(d, 180);
      case "anual": return addDays(d, 365);
      default: return addDays(d, 30);
    }
  };

  // Helper to format dates to normal front-end DD/MM/YYYY
  const formatDateForFront = (dateStr) => {
    if (!dateStr) return "";
    try {
      const dateOnly = dateStr.includes("T") ? dateStr.split("T")[0] : dateStr.split(" ")[0];
      const parts = dateOnly.split("-");
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
    } catch (e) {}
    return dateStr;
  };

  // Sync Alerts Routine
  const syncAlertsRoutine = async (machList, maintList) => {
    const today = new Date();
    today.setHours(0,0,0,0);

    for (const maint of maintList) {
      const nextExecDateStr = maint.data_proxima_execucao || calculateNextDate(maint.data_ultima_execucao, maint.frequencia);
      const nextDate = new Date(nextExecDateStr);
      nextDate.setHours(0,0,0,0);

      // Calcular dias restantes
      const diffTime = nextDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      // Determinar prioridade
      let priority = "baixa";
      if (diffDays <= 0) priority = "critica";
      else if (diffDays <= 3) priority = "alta";
      else if (diffDays <= 7) priority = "media";

      // Determinar status execução
      const statusExec = diffDays < 0 ? "atrasada" : "pendente";

      // 1. Atualiza a manutenção se mudou de status
      if (maint.status_execucao !== statusExec || maint.data_proxima_execucao !== nextExecDateStr) {
        try {
          await pb.collection("manutencoes").update(maint.id, {
            status_execucao: statusExec,
            data_proxima_execucao: nextExecDateStr
          });
        } catch (e) {
          console.error("Maint update failed inside sync:", e);
        }
      }

      // 2. Criar ou atualizar alerta ativo
      try {
        const existingAlerts = await pb.collection("alertas_manutencao").getFullList({
          filter: `manutencao_id='${maint.id}' && status='ativo'`
        });

        if (existingAlerts.length > 0) {
          const alertRecord = existingAlerts[0];
          if (alertRecord.dias_restantes !== diffDays || alertRecord.prioridade !== priority) {
            await pb.collection("alertas_manutencao").update(alertRecord.id, {
              dias_restantes: diffDays,
              prioridade: priority,
              data_prevista: nextExecDateStr
            });
          }
        } else if (diffDays <= 7) {
          // Cria alerta novo apenas se faltar <= 7 dias ou estiver atrasado
          await pb.collection("alertas_manutencao").create({
            maquina_id: maint.maquina_id,
            manutencao_id: maint.id,
            tipo_alerta: diffDays < 0 ? "manutencao_atrasada" : "proxima_manutencao",
            data_prevista: nextExecDateStr,
            dias_restantes: diffDays,
            prioridade: priority,
            status: "ativo"
          });
        }
      } catch (err) {
        console.error("Alert sync failed:", err);
      }
    }
  };

  // Selected Machine Details
  const activeMachine = useMemo(() => machines.find(m => m.id === activeMachineId), [activeMachineId, machines]);
  const activeMaintenances = useMemo(() => maintenances.filter(m => m.maquina_id === activeMachineId), [activeMachineId, maintenances]);

  const handleEditMachine = (machine) => {
    setMachForm({
      nome: machine.nome || "",
      marca: machine.marca || "",
      modelo: machine.modelo || "",
      numero_serie: machine.numero_serie || "",
      data_aquisicao: machine.data_aquisicao || "",
      valor_aquisicao: machine.valor_aquisicao || "",
      fornecedor: machine.fornecedor || "",
      localizacao: machine.localizacao || "",
      status: machine.status || "ativa",
      descricao: machine.descricao || "",
      especificacoes: machine.especificacoes || ""
    });
    setEditingMachineId(machine.id);
    setIsMachineModalOpen(true);
  };

  // Handle Save Machine
  const saveMachine = async (e) => {
    e.preventDefault();
    try {
      if (editingMachineId) {
        const record = await pb.collection("maquinas").update(editingMachineId, machForm);
        setMachines(prev => prev.map(m => m.id === editingMachineId ? record : m));
        setIsMachineModalOpen(false);
        setEditingMachineId(null);
        setMachForm({
          nome: "", marca: "", modelo: "", numero_serie: "",
          data_aquisicao: "", valor_aquisicao: "", fornecedor: "",
          localizacao: "", status: "ativa", descricao: "", especificacoes: ""
        });
        alert("Equipamento atualizado com sucesso!");
      } else {
        const record = await pb.collection("maquinas").create(machForm);
        setMachines(prev => [record, ...prev]);
        setActiveMachineId(record.id);
        setIsMachineModalOpen(false);
        setMachForm({
          nome: "", marca: "", modelo: "", numero_serie: "",
          data_aquisicao: "", valor_aquisicao: "", fornecedor: "",
          localizacao: "", status: "ativa", descricao: "", especificacoes: ""
        });
        alert("Máquina cadastrada com sucesso!");
      }
    } catch (err) {
      alert("Erro ao salvar máquina: " + err.message);
    }
  };

  const handleEditMaintPlan = (plan) => {
    setMaintForm({
      tipo: plan.tipo || "preventiva",
      frequencia: plan.frequencia || "mensal",
      descricao: plan.descricao || "",
      responsavel: plan.responsavel || "",
      data_ultima_execucao: plan.data_ultima_execucao || new Date().toISOString().split("T")[0]
    });
    setEditingMaintPlanId(plan.id);
    setIsMaintModalOpen(true);
  };

  const handleDeleteMaintPlan = async (planId) => {
    if (!window.confirm("Deseja realmente excluir este plano de manutenção? Esta ação não pode ser desfeita.")) return;
    try {
      await pb.collection("manutencoes").delete(planId);
      
      // Exclui alertas ativos associados a esse plano
      const activeAlerts = await pb.collection("alertas_manutencao").getFullList({
        filter: `manutencao_id='${planId}'`
      });
      for (const al of activeAlerts) {
        await pb.collection("alertas_manutencao").delete(al.id);
      }

      setMaintenances(prev => prev.filter(m => m.id !== planId));
      alert("Plano de manutenção excluído com sucesso!");
      loadData();
    } catch (err) {
      alert("Erro ao excluir plano de manutenção: " + err.message);
    }
  };

  // Handle Save Maintenance Plan
  const saveMaintenancePlan = async (e) => {
    e.preventDefault();
    if (!activeMachineId) return;
    
    const nextDate = calculateNextDate(maintForm.data_ultima_execucao, maintForm.frequencia);

    try {
      if (editingMaintPlanId) {
        const record = await pb.collection("manutencoes").update(editingMaintPlanId, {
          ...maintForm,
          data_proxima_execucao: nextDate
        });
        setMaintenances(prev => prev.map(m => m.id === editingMaintPlanId ? record : m));
        setIsMaintModalOpen(false);
        setEditingMaintPlanId(null);
        setMaintForm({
          tipo: "preventiva", frequencia: "mensal", descricao: "",
          responsavel: "", data_ultima_execucao: new Date().toISOString().split("T")[0]
        });
        alert("Plano de manutenção atualizado!");
      } else {
        const record = await pb.collection("manutencoes").create({
          maquina_id: activeMachineId,
          ...maintForm,
          data_proxima_execucao: nextDate,
          status_execucao: "pendente",
          historico: []
        });

        setMaintenances(prev => [record, ...prev]);
        setIsMaintModalOpen(false);
        setMaintForm({
          tipo: "preventiva", frequencia: "mensal", descricao: "",
          responsavel: "", data_ultima_execucao: new Date().toISOString().split("T")[0]
        });
        alert("Plano de manutenção cadastrado!");
      }
      loadData(); // Reload para recalcular os alertas
    } catch (err) {
      alert("Erro ao salvar plano: " + err.message);
    }
  };

  // Mark Maintenance as Executed
  const executeMaintenance = async (e) => {
    e.preventDefault();
    if (!activeMaintPlan) return;

    const todayStr = new Date().toISOString().split("T")[0];
    const nextDateStr = calculateNextDate(todayStr, activeMaintPlan.frequencia);

    const newLog = {
      data: todayStr,
      executado_por: execForm.executado_por,
      observacoes: execForm.observacoes,
      duracao_minutos: Number(execForm.duracao_minutos) || 30
    };

    const currentHistory = Array.isArray(activeMaintPlan.historico) ? activeMaintPlan.historico : [];

    try {
      // 1. Atualiza o plano de manutenção
      await pb.collection("manutencoes").update(activeMaintPlan.id, {
        data_ultima_execucao: todayStr,
        data_proxima_execucao: nextDateStr,
        status_execucao: "pendente",
        historico: [...currentHistory, newLog]
      });

      // 2. Atualiza a máquina com a data da última manutenção
      await pb.collection("maquinas").update(activeMaintPlan.maquina_id, {
        ultima_manutencao: todayStr,
        proxima_manutencao: nextDateStr
      });

      // 3. Resolve todos os alertas ativos deste plano
      const activeAlerts = await pb.collection("alertas_manutencao").getFullList({
        filter: `manutencao_id='${activeMaintPlan.id}' && status='ativo'`
      });

      for (const alertRecord of activeAlerts) {
        await pb.collection("alertas_manutencao").update(alertRecord.id, {
          status: "resolvido",
          data_resolucao: new Date().toISOString()
        });
      }

      setIsExecModalOpen(false);
      setExecForm({ executado_por: user?.name || "", observacoes: "", duracao_minutos: "30" });
      alert("Manutenção registrada e agendamentos atualizados!");
      loadData(); // Recarrega
    } catch (err) {
      alert("Erro ao registrar manutenção: " + err.message);
    }
  };

  // Direct confirmation of alert from alert dashboard
  const handleResolveAlertDirectly = async (alertRecord) => {
    const plan = maintenances.find(m => m.id === alertRecord.manutencao_id);
    if (!plan) return;
    setActiveMaintPlan(plan);
    setIsExecModalOpen(true);
  };

  // Delete Machine
  const handleDeleteMachine = async (id) => {
    if (!window.confirm("Remover esta máquina e todos os seus planos associados?")) return;
    try {
      // 1. Delete associated alerts
      const machineAlerts = await pb.collection("alertas_manutencao").getFullList({
        filter: `maquina_id='${id}'`
      });
      for (const al of machineAlerts) {
        await pb.collection("alertas_manutencao").delete(al.id);
      }

      // 2. Delete associated maintenance plans
      const associatedPlans = await pb.collection("manutencoes").getFullList({
        filter: `maquina_id='${id}'`
      });
      for (const p of associatedPlans) {
        await pb.collection("manutencoes").delete(p.id);
      }

      // 3. Delete the machine
      await pb.collection("maquinas").delete(id);
      
      // 4. Update local states
      setMachines(prev => prev.filter(m => m.id !== id));
      setMaintenances(prev => prev.filter(m => m.maquina_id !== id));
      setAlerts(prev => prev.filter(a => a.maquina_id !== id));

      if (activeMachineId === id) {
        const remaining = machines.filter(m => m.id !== id);
        setActiveMachineId(remaining[0]?.id || null);
      }
      alert("Máquina e todos os planos e alertas associados foram removidos.");
    } catch (e) {
      alert("Erro ao excluir: " + e.message);
    }
  };

  // Filters logic
  const filteredMachines = useMemo(() => {
    return machines.filter(m => {
      const matchSearch = m.nome.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          m.marca.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          m.modelo.toLowerCase().includes(searchTerm.toLowerCase());
      const matchStatus = statusFilter === "all" ? true : m.status === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [machines, searchTerm, statusFilter]);

  return (
    <div className="flex-1 flex flex-col md:flex-row h-full overflow-hidden bg-slate-50">
      
      {/* LEFT COLUMN: MACHINES LIST */}
      <div className="w-full md:w-80 border-r border-slate-200 bg-white flex flex-col shrink-0">
        
        {/* Header */}
        <div className="p-4 border-b border-slate-200 space-y-3 shrink-0">
          <div className="flex justify-between items-center">
            <h3 className="font-bold text-slate-700 flex items-center gap-1.5 text-sm">
              <Wrench size={16} className="text-blue-500" /> Equipamentos
            </h3>
            <button
              onClick={() => {
                setEditingMachineId(null);
                setMachForm({
                  nome: "", marca: "", modelo: "", numero_serie: "",
                  data_aquisicao: "", valor_aquisicao: "", fornecedor: "",
                  localizacao: "", status: "ativa", descricao: "", especificacoes: ""
                });
                setIsMachineModalOpen(true);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white p-1 rounded hover:shadow-sm"
              title="Nova Máquina"
            >
              <Plus size={16} />
            </button>
          </div>

          <input
            type="text"
            placeholder="Buscar equipamento..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-xs outline-none focus:border-blue-500"
          />

          <div className="flex gap-1.5">
            {["all", "ativa", "em_manutencao", "inativa"].map((st) => (
              <button
                key={st}
                onClick={() => setStatusFilter(st)}
                className={`px-2 py-1 text-[9px] font-extrabold rounded-md uppercase border transition-all ${
                  statusFilter === st
                    ? "bg-slate-800 text-white border-slate-800"
                    : "bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100"
                }`}
              >
                {st === "all" ? "Todos" : st.replace("_", " ")}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto custom-scrollbar p-2 space-y-1.5 bg-slate-50/50">
          {filteredMachines.map(m => {
            const isActive = m.id === activeMachineId;
            const machAlerts = alerts.filter(a => a.maquina_id === m.id);

            return (
              <div
                key={m.id}
                onClick={() => setActiveMachineId(m.id)}
                className={`p-3 rounded-xl border transition-all cursor-pointer flex justify-between items-center relative ${
                  isActive
                    ? "bg-white border-blue-500 shadow-md ring-1 ring-blue-500"
                    : "bg-white border-slate-200 hover:border-slate-300"
                }`}
              >
                <div className="min-w-0 flex-1">
                  <h4 className="font-bold text-xs text-slate-800 truncate">{m.nome}</h4>
                  <span className="text-[10px] text-slate-400 font-semibold">{m.marca} {m.modelo}</span>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className={`px-1.5 py-0.5 rounded text-[8px] font-extrabold uppercase ${
                      m.status === "ativa"
                        ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                        : m.status === "em_manutencao"
                        ? "bg-amber-50 text-amber-700 border border-amber-200 animate-pulse"
                        : "bg-slate-100 text-slate-500 border border-slate-200"
                    }`}>
                      {m.status}
                    </span>
                    {m.localizacao && (
                      <span className="text-[9px] text-slate-400 font-medium flex items-center gap-0.5">
                        <MapPin size={8} /> {m.localizacao}
                      </span>
                    )}
                  </div>
                </div>

                {machAlerts.length > 0 && (
                  <span className="h-5 w-5 bg-red-500 text-white rounded-full flex items-center justify-center text-[10px] font-black animate-bounce shrink-0 shadow-sm">
                    {machAlerts.length}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* CENTER AREA: ALERTS & MACHINE PLAN & TIMELINE */}
      <div className="flex-1 flex flex-col overflow-y-auto custom-scrollbar p-6 space-y-6">
        
        {/* ALERTS SECTION */}
        {alerts.length > 0 && (
          <div className="bg-white rounded-2xl p-5 border border-red-200 shadow-sm">
            <h3 className="text-sm font-bold text-red-700 flex items-center gap-1.5 mb-3">
              <ShieldAlert size={18} className="animate-bounce" /> Alertas de Manutenção Pendentes
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {alerts.map(al => {
                const mach = machines.find(m => m.id === al.maquina_id);
                const plan = maintenances.find(p => p.id === al.manutencao_id);

                return (
                  <div
                    key={al.id}
                    className={`p-3 rounded-xl border flex items-center justify-between text-xs font-semibold ${
                      al.prioridade === "critica"
                        ? "bg-red-50 border-red-200 text-red-900"
                        : al.prioridade === "alta"
                        ? "bg-orange-50 border-orange-200 text-orange-900"
                        : "bg-yellow-50 border-yellow-200 text-yellow-900"
                    }`}
                  >
                    <div>
                      <span className="font-black block uppercase text-[9px] tracking-wide opacity-80">
                        {al.prioridade} • {al.dias_restantes < 0 ? `ATRASADO ${Math.abs(al.dias_restantes)} DIAS` : `Faltam ${al.dias_restantes} dias`}
                      </span>
                      <span className="font-bold">{mach?.nome}</span>
                      <p className="text-[10px] opacity-90 truncate max-w-xs">{plan?.descricao}</p>
                    </div>

                    <button
                      onClick={() => handleResolveAlertDirectly(al)}
                      className="bg-slate-800 hover:bg-slate-900 text-white px-2 py-1 rounded text-[10px] font-bold shadow-sm transition-all"
                    >
                      Executar
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ACTIVE MACHINE GENERAL DETAIL */}
        {activeMachine ? (
          <>
            <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm flex flex-col md:flex-row justify-between gap-4">
              <div>
                <h2 className="text-xl font-extrabold text-slate-800 flex items-center gap-2">
                  {activeMachine.nome}
                </h2>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs font-semibold text-slate-500">
                  <span>Marca: <strong className="text-slate-700">{activeMachine.marca || "N/D"}</strong></span>
                  <span>Modelo: <strong className="text-slate-700">{activeMachine.modelo || "N/D"}</strong></span>
                  <span>Série: <strong className="text-slate-700 font-mono">{activeMachine.numero_serie || "N/D"}</strong></span>
                  {activeMachine.localizacao && <span>Local: <strong className="text-slate-700">{activeMachine.localizacao}</strong></span>}
                </div>
                {activeMachine.descricao && (
                  <p className="text-xs text-slate-500 mt-3 bg-slate-50 p-2.5 rounded-lg border border-slate-200/60 leading-relaxed">
                    {activeMachine.descricao}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-2 shrink-0 md:text-right md:justify-center">
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setEditingMaintPlanId(null);
                      setMaintForm({
                        tipo: "preventiva", frequencia: "mensal", descricao: "",
                        responsavel: "", data_ultima_execucao: new Date().toISOString().split("T")[0]
                      });
                      setIsMaintModalOpen(true);
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-3.5 py-2 rounded-xl text-xs font-bold transition-all shadow-sm flex items-center gap-1"
                  >
                    <Plus size={14} /> Novo Plano
                  </button>
                  <button
                    onClick={() => handleEditMachine(activeMachine)}
                    className="bg-amber-50 hover:bg-amber-100 text-amber-600 border border-amber-200 p-2 rounded-xl transition-all"
                    title="Editar Equipamento"
                  >
                    <Edit3 size={14} />
                  </button>
                  <button
                    onClick={() => handleDeleteMachine(activeMachine.id)}
                    className="bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 p-2 rounded-xl transition-all"
                    title="Excluir Equipamento"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </div>

            {/* MAINTENANCE PLAN LIST */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-4">
              <h3 className="font-extrabold text-slate-700 flex items-center gap-2 border-b border-slate-100 pb-3 text-sm">
                <Calendar size={18} className="text-blue-500" /> Planos Preventivos Ativos
              </h3>

              {activeMaintenances.length > 0 ? (
                <div className="space-y-3">
                  {activeMaintenances.map(plan => {
                    const isOverdue = plan.status_execucao === "atrasada";
                    return (
                      <div
                        key={plan.id}
                        className={`p-4 rounded-xl border flex flex-col md:flex-row md:items-center justify-between gap-4 transition-all ${
                          isOverdue
                            ? "bg-red-50/20 border-red-200"
                            : "bg-slate-50/30 border-slate-200"
                        }`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-slate-800 text-sm">{plan.descricao}</span>
                            <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded text-[9px] font-bold uppercase">
                              {plan.frequencia}
                            </span>
                            {isOverdue && (
                              <span className="px-1.5 py-0.5 bg-red-600 text-white rounded text-[9px] font-black uppercase animate-pulse flex items-center gap-0.5">
                                <AlertTriangle size={8} /> Atrasada
                              </span>
                            )}
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2.5 text-[10px] font-semibold text-slate-500">
                            <span>Última: <strong className="text-slate-700 font-mono">{formatDateForFront(plan.data_ultima_execucao) || "Nunca"}</strong></span>
                            <span>Próxima: <strong className={`font-mono ${isOverdue ? "text-red-600 font-black" : "text-slate-700"}`}>{formatDateForFront(plan.data_proxima_execucao) || "N/A"}</strong></span>
                            <span>Resp: <strong className="text-slate-700">{plan.responsavel || "Interno"}</strong></span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setActiveMaintPlan(plan);
                              setIsExecModalOpen(true);
                            }}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-3 py-2 rounded-xl text-xs flex items-center gap-1 shadow-sm transition-all"
                            title="Registrar Execução"
                          >
                            <CheckCircle size={14} /> Feito
                          </button>
                          <button
                            onClick={() => handleEditMaintPlan(plan)}
                            className="bg-amber-50 hover:bg-amber-100 text-amber-600 border border-amber-200 p-2 rounded-xl transition-all"
                            title="Editar Plano"
                          >
                            <Edit3 size={14} />
                          </button>
                          <button
                            onClick={() => handleDeleteMaintPlan(plan.id)}
                            className="bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 p-2 rounded-xl transition-all"
                            title="Excluir Plano"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="h-32 flex flex-col items-center justify-center text-slate-400 text-center">
                  <AlertCircle size={28} className="mb-1 text-slate-300" />
                  <span className="text-xs font-semibold">Sem planos de manutenção cadastrados para esta máquina.</span>
                </div>
              )}
            </div>

            {/* MAINTENANCE HISTORIC / TIMELINE */}
            <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm space-y-4">
              <h3 className="font-extrabold text-slate-700 flex items-center gap-2 border-b border-slate-100 pb-3 text-sm">
                <History size={18} className="text-blue-500" /> Linha do Tempo de Intervenções
              </h3>

              {(() => {
                // Flatten logs of all plans
                const allLogs = [];
                activeMaintenances.forEach(plan => {
                  if (Array.isArray(plan.historico)) {
                    plan.historico.forEach(log => {
                      allLogs.push({
                        ...log,
                        planName: plan.descricao,
                        planType: plan.tipo
                      });
                    });
                  }
                });

                // Sort logs by date descending
                allLogs.sort((a, b) => new Date(b.data).getTime() - new Date(a.data).getTime());

                if (allLogs.length > 0) {
                  return (
                    <div className="relative border-l-2 border-slate-150 pl-5 ml-3.5 space-y-5">
                      {allLogs.map((log, idx) => (
                        <div key={idx} className="relative">
                          {/* Node bubble */}
                          <span className="absolute -left-[29px] top-1 h-4 w-4 bg-emerald-100 text-emerald-600 border border-emerald-400 rounded-full flex items-center justify-center">
                            <CheckCircle size={10} />
                          </span>

                          <div className="p-3 bg-slate-50/50 rounded-xl border border-slate-200/60 max-w-xl">
                            <div className="flex justify-between items-center mb-1">
                              <span className="font-extrabold text-xs text-slate-800">{log.planName}</span>
                              <span className="text-[10px] font-bold text-slate-400 font-mono">{formatDateForFront(log.data)}</span>
                            </div>
                            <p className="text-xs text-slate-500 leading-relaxed">{log.observacoes || "Nenhuma observação informada."}</p>
                            <div className="flex gap-4 mt-2 text-[9px] font-bold text-slate-400">
                              <span>Executado por: <strong className="text-slate-600">{log.executado_por}</strong></span>
                              <span>Duração: <strong className="text-slate-600">{log.duracao_minutos} min</strong></span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                } else {
                  return (
                    <div className="h-24 flex items-center justify-center text-slate-400 text-center text-xs font-semibold">
                      Nenhum histórico de execução registrado até o momento.
                    </div>
                  );
                }
              })()}
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-slate-400 text-center">
            <Wrench size={48} className="mb-2 text-slate-300" />
            <span className="text-sm font-semibold">Cadastre ou selecione um equipamento na barra lateral.</span>
          </div>
        )}
      </div>

      {/* MODAL 1: NEW/EDIT MACHINE */}
      {isMachineModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl border border-slate-200 flex flex-col max-h-[85vh] animate-scale-up">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center shrink-0">
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-1">
                <Wrench size={18} className="text-blue-500" />
                {editingMachineId ? "Editar Equipamento" : "Cadastrar Equipamento"}
              </h3>
              <button onClick={() => setIsMachineModalOpen(false)} className="text-slate-400 hover:text-slate-600 font-bold">✕</button>
            </div>

            <form onSubmit={saveMachine} className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Nome do Equipamento *</label>
                  <input
                    type="text" required
                    value={machForm.nome}
                    onChange={(e) => setMachForm({ ...machForm, nome: e.target.value })}
                    placeholder="Ex: Injetora de Cera Vácuo"
                    className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 font-bold text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Marca</label>
                  <input
                    type="text"
                    value={machForm.marca}
                    onChange={(e) => setMachForm({ ...machForm, marca: e.target.value })}
                    className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Modelo</label>
                  <input
                    type="text"
                    value={machForm.modelo}
                    onChange={(e) => setMachForm({ ...machForm, modelo: e.target.value })}
                    className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Número de Série</label>
                  <input
                    type="text"
                    value={machForm.numero_serie}
                    onChange={(e) => setMachForm({ ...machForm, numero_serie: e.target.value })}
                    className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 font-mono text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Localização</label>
                  <input
                    type="text"
                    value={machForm.localizacao}
                    onChange={(e) => setMachForm({ ...machForm, localizacao: e.target.value })}
                    placeholder="Ex: Fundição, Acabamento"
                    className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Status</label>
                  <select
                    value={machForm.status}
                    onChange={(e) => setMachForm({ ...machForm, status: e.target.value })}
                    className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold bg-white"
                  >
                    <option value="ativa">Ativa</option>
                    <option value="em_manutencao">Em Manutenção</option>
                    <option value="inativa">Inativa</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Data de Aquisição</label>
                  <input
                    type="date"
                    value={machForm.data_aquisicao}
                    onChange={(e) => setMachForm({ ...machForm, data_aquisicao: e.target.value })}
                    className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 font-mono text-xs"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Valor de Compra (R$)</label>
                  <input
                    type="number"
                    value={machForm.valor_aquisicao}
                    onChange={(e) => setMachForm({ ...machForm, valor_aquisicao: e.target.value })}
                    className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Especificações Técnicas</label>
                  <input
                    type="text"
                    value={machForm.especificacoes}
                    onChange={(e) => setMachForm({ ...machForm, especificacoes: e.target.value })}
                    placeholder="Ex: Voltagem 220v, Ar Comprimido 6bar"
                    className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Descrição</label>
                  <textarea
                    value={machForm.descricao}
                    onChange={(e) => setMachForm({ ...machForm, descricao: e.target.value })}
                    rows={2}
                    className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                  />
                </div>
              </div>

              <div className="pt-4 border-t border-slate-100 flex justify-end gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setIsMachineModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg text-xs shadow-sm"
                >
                  {editingMachineId ? "Salvar Alterações" : "Cadastrar Equipamento"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 2: NEW/EDIT PLAN */}
      {isMaintModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl border border-slate-200 flex flex-col animate-scale-up">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center shrink-0">
              <h3 className="text-sm font-bold text-slate-800">
                {editingMaintPlanId ? "Editar Plano Preventivo" : "Novo Plano Preventivo"}
              </h3>
              <button onClick={() => setIsMaintModalOpen(false)} className="text-slate-400 hover:text-slate-600 font-bold">✕</button>
            </div>

            <form onSubmit={saveMaintenancePlan} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Descrição do Plano *</label>
                <input
                  type="text" required
                  value={maintForm.descricao}
                  onChange={(e) => setMaintForm({ ...maintForm, descricao: e.target.value })}
                  placeholder="Ex: Troca de óleo de bomba de vácuo"
                  className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Frequência</label>
                  <select
                    value={maintForm.frequencia}
                    onChange={(e) => setMaintForm({ ...maintForm, frequencia: e.target.value })}
                    className="w-full p-2 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold bg-white"
                  >
                    <option value="diaria">Diária</option>
                    <option value="semanal">Semanal</option>
                    <option value="mensal">Mensal</option>
                    <option value="trimestral">Trimestral</option>
                    <option value="semestral">Semestral</option>
                    <option value="anual">Anual</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Responsável</label>
                  <input
                    type="text"
                    value={maintForm.responsavel}
                    onChange={(e) => setMaintForm({ ...maintForm, responsavel: e.target.value })}
                    placeholder="Ex: Oficina"
                    className="w-full p-2 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-semibold"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Última Execução</label>
                <input
                  type="date"
                  value={maintForm.data_ultima_execucao}
                  onChange={(e) => setMaintForm({ ...maintForm, data_ultima_execucao: e.target.value })}
                  className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 font-mono text-xs"
                />
              </div>

              <div className="pt-4 border-t border-slate-100 flex justify-end gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setIsMaintModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg text-xs shadow-sm"
                >
                  {editingMaintPlanId ? "Salvar Alterações" : "Agendar Plano"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL 3: EXECUTE MAINTENANCE */}
      {isExecModalOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl border border-slate-200 flex flex-col animate-scale-up">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center shrink-0">
              <h3 className="text-sm font-bold text-slate-800">Registrar Manutenção Realizada</h3>
              <button onClick={() => setIsExecModalOpen(false)} className="text-slate-400 hover:text-slate-600 font-bold">✕</button>
            </div>

            <form onSubmit={executeMaintenance} className="p-6 space-y-4">
              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 text-xs font-semibold text-slate-600">
                Plano: <strong className="text-slate-800">{activeMaintPlan?.descricao}</strong>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Executado Por *</label>
                <input
                  type="text" required
                  value={execForm.executado_por}
                  onChange={(e) => setExecForm({ ...execForm, executado_por: e.target.value })}
                  placeholder="Seu nome"
                  className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs font-bold"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Duração (minutos)</label>
                <input
                  type="number"
                  required
                  value={execForm.duracao_minutos}
                  onChange={(e) => setExecForm({ ...execForm, duracao_minutos: e.target.value })}
                  className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 font-mono text-xs"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1">Observações / Peças Trocadas</label>
                <textarea
                  value={execForm.observacoes}
                  onChange={(e) => setExecForm({ ...execForm, observacoes: e.target.value })}
                  placeholder="Ex: Trocado filtro e óleo. Limpeza geral realizada."
                  rows={3}
                  className="w-full p-2.5 border border-slate-300 rounded-lg outline-none focus:ring-1 focus:ring-blue-500 text-xs"
                />
              </div>

              <div className="pt-4 border-t border-slate-100 flex justify-end gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setIsExecModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-500 hover:bg-slate-100 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-lg text-xs shadow-sm"
                >
                  Registrar Execução
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
